import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell, ChevronDown, LogOut, Menu, UserCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useShell } from "@/components/layout/ShellContext";
import { NAV, pageTitle } from "@/components/layout/nav";
import { Avatar } from "@/components/ui";
import { ROLE_LABEL } from "@/lib/format";

export default function Header() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { setSidebarOpen, sidebarCollapsed, setSidebarCollapsed } = useShell();
  const [menu, setMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => menuRef.current && !menuRef.current.contains(e.target) && setMenu(false);
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const go = (to) => { setMenu(false); navigate(to); };
  const canSeeNotifications = user.role !== "employee";

  return (
    <header className="sticky top-0 z-30 bg-surface">
      <div className="flex items-center gap-3 px-4 lg:px-5 h-12">
        <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-xl neu-pressable text-brand-blue" aria-label="Open menu">
          <Menu size={18} />
        </button>
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="hidden lg:inline-flex p-2.5 rounded-xl neu-pressable text-brand-blue"
          title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <Menu size={16} />
        </button>

        <h1 className="min-w-0 truncate text-base font-extrabold text-brand-blue leading-tight tracking-tight">
          {pageTitle(NAV[user.role] || [], pathname)}
        </h1>

        <div className="flex items-center gap-2.5 ml-auto">
          {canSeeNotifications && (
            <button onClick={() => go("/notifications")} className="relative p-2 rounded-xl neu-pressable text-brand-blue" aria-label="Notifications">
              <Bell size={16} />
              <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-brand-orange rounded-full animate-pulse-soft" />
            </button>
          )}

          <div ref={menuRef} className="relative">
            <button onClick={() => setMenu(!menu)} className="flex items-center gap-2 p-1 pr-2.5 rounded-full neu-pressable" aria-haspopup="menu" aria-expanded={menu}>
              <Avatar name={user.name} size="sm" brand />
              <ChevronDown size={13} className="text-slate-500 hidden md:block" />
            </button>

            {menu && (
              <div role="menu" className="absolute right-0 top-full mt-2 w-60 neu-card animate-scale-in z-50 py-2">
                <div className="px-3 py-2 mb-1">
                  <p className="text-sm font-bold text-brand-blue">{user.name}</p>
                  <p className="text-xs text-slate-500">{user.email}</p>
                  <span className="badge mt-1.5">{ROLE_LABEL[user.role]}</span>
                </div>
                <div className="neu-divider mx-3 my-1" />
                {user.employee_id && (
                  <button role="menuitem" onClick={() => go("/profile")} className="w-[calc(100%-0.5rem)] flex items-center gap-2.5 px-3 py-2 mx-1 rounded-lg text-sm text-brand-blue hover:shadow-neu-xs transition-all text-left">
                    <UserCircle size={14} /> My Profile
                  </button>
                )}
                <div className="neu-divider mx-3 my-1" />
                <button role="menuitem" onClick={() => { setMenu(false); logout(); navigate("/login"); }} className="w-[calc(100%-0.5rem)] flex items-center gap-2.5 px-3 py-2 mx-1 rounded-lg text-sm text-rose-600 hover:shadow-neu-xs transition-all text-left">
                  <LogOut size={14} /> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
